/**
 * Auth Models - matching the Rust API
 */

// === Request Types ===

export interface LoginRequest {
	email: string;
	password: string;
	totp_code?: string; // Optional 2FA code
}

export interface RegisterRequest {
	username?: string; // Optional - auto-generated from email if not provided
	email: string;
	password: string;
	name?: string | null;
	marketing_consent?: boolean;
}

export interface RefreshRequest {
	refresh_token: string;
}

export interface LogoutRequest {
	refresh_token: string;
}

export interface ForgotPasswordRequest {
	email: string;
}

export interface ResetPasswordRequest {
	token: string;
	new_password: string;
}

export interface VerifyEmailRequest {
	token: string;
}

export interface ResendVerificationRequest {
	email: string;
}

// === User Profile Request Types ===

export interface ChangePasswordRequest {
	current_password: string;
	new_password: string;
}

export interface UpdateProfileRequest {
	name?: string | null;
}

export interface ChangeEmailRequest {
	new_email: string;
	password: string;
}

// === 2FA Request Types ===

export interface Setup2FARequest {
	password: string;
}

export interface Verify2FARequest {
	code: string;
	secret: string;
	recovery_codes: string[];
}

export interface Disable2FARequest {
	password: string;
	code: string; // TOTP code or recovery code
}

// === Response Types ===

export interface AuthResponse {
	access_token: string;
	refresh_token: string;
	token_type: string;
	expires_in: number;
	user: UserResponse;
}

export interface TokenResponse {
	access_token: string;
	refresh_token: string;
	token_type: string;
	expires_in: number;
}

export interface UserResponse {
	id: string;
	username: string;
	email: string;
	name?: string | null;
	role: string;
	is_active: boolean;
	is_verified: boolean;
	totp_enabled: boolean;
	created_at: string;
	account_type: 'private' | 'business';
	/** Subscription tier gating which modules the user can access. New per-module model uses
	 *  `demo`/`climate`; `starter`/`pro`/`full` are legacy packages. */
	tier?: 'demo' | 'climate' | 'starter' | 'pro' | 'full';
	/** Moduły z dostępem (startowe + odblokowane; admin — wszystkie) — jedyne źródło dostępu. */
	modules?: string[];
}

export interface BusinessProfile {
	id: string;
	company_name: string;
	legal_form?: string | null;
	nip?: string | null;
	vat_eu?: string | null;
	industry?: string | null;
	address_street?: string | null;
	address_city?: string | null;
	address_country?: string | null;
	address_postal?: string | null;
	verified_at?: string | null;
	created_at: string;
	updated_at: string;
}

export interface UpsertBusinessProfileRequest {
	company_name: string;
	legal_form?: string | null;
	nip?: string | null;
	vat_eu?: string | null;
	industry?: string | null;
	address_street?: string | null;
	address_city?: string | null;
	address_country?: string | null;
	address_postal?: string | null;
}

export type InvestmentExperience = 'beginner' | 'intermediate' | 'advanced' | 'professional';
export type RiskTolerance = 'conservative' | 'moderate' | 'aggressive' | 'speculative';
export type InvestmentGoal = 'long_term_growth' | 'passive_income' | 'trading' | 'speculation';

export interface InvestmentProfile {
	investment_experience: InvestmentExperience | null;
	risk_tolerance: RiskTolerance | null;
	investment_goals: InvestmentGoal[];
	advisor_disclaimer_accepted_at?: string | null;
}

export interface UpsertInvestmentProfileRequest {
	investment_experience: InvestmentExperience;
	risk_tolerance: RiskTolerance;
	investment_goals: InvestmentGoal[];
	accept_disclaimer?: boolean;
}

export interface AdvisorEligibility {
	eligible: boolean;
	missing: string[];
	has_accepted_disclaimer: boolean;
}

export interface MessageResponse {
	message: string;
}

export interface RegisterResponse {
	message: string;
	email: string;
	requires_verification: boolean;
}

export interface ErrorResponse {
	error: string;
	code?: string;
}

// === 2FA Response Types ===

export interface Setup2FAResponse {
	secret: string;
	otpauth_url: string;
	recovery_codes: string[];
}

export interface Status2FAResponse {
	enabled: boolean;
	recovery_codes_remaining: number;
}

// === Security/Audit Types ===

export interface AuditLogEntry {
	id: number;
	action: string;
	ip_address?: string;
	user_agent?: string;
	success?: boolean;
	failure_reason?: string;
	entity_type?: string;
	entity_id?: string;
	created_at: string;
}

export interface AuditLogsResponse {
	logs: AuditLogEntry[];
	limit: number;
	offset: number;
}

export interface SecurityEventsResponse {
	events: AuditLogEntry[];
}

// === Session Types ===

export interface SessionEntry {
	id: string;
	ip_address: string;
	browser?: string;
	os?: string;
	device_type: string;
	location?: string;
	created_at: string;
	last_activity?: string;
	is_current: boolean;
}

export interface SessionsResponse {
	sessions: SessionEntry[];
	total: number;
}

// === Login History Types ===

export interface LoginHistoryEntry {
	timestamp: string;
	ip_address: string;
	device_info: string;
	success: boolean;
	location?: string;
}

export interface LoginHistoryResponse {
	history: LoginHistoryEntry[];
}

// === Security Overview Types ===

export interface SecurityOverviewResponse {
	score: number;
	email_verified: boolean;
	two_factor_enabled: boolean;
	active_sessions: number;
	recent_logins: number;
	failed_logins: number;
	last_password_change?: string;
	recommendations: string[];
}

// === GDPR Types ===

export interface GdprDataExportResponse {
	metadata: {
		exported_at: string;
		format_version: string;
		user_id: string;
	};
	profile: {
		email: string;
		created_at: string;
		last_login_at?: string;
		email_verified: boolean;
		totp_enabled: boolean;
		status: string;
	};
	preferences: {
		marketing_consent: boolean;
		consent_recorded_at?: string;
	};
	activity: Array<{
		action: string;
		ip_address?: string;
		user_agent?: string;
		success?: boolean;
		created_at: string;
	}>;
	sessions: Array<{
		device_info: string;
		ip_address?: string;
		created_at: string;
		last_activity: string;
	}>;
	favorites: string[];
}

export interface GdprDeletionResponse {
	request_id: string;
	status: string;
	scheduled_deletion_at: string;
	message: string;
}

export interface GdprConsentResponse {
	message: string;
	marketing_consent: boolean;
}

// === Auth State ===

export interface AuthState {
	user: UserResponse | null;
	accessToken: string | null;
	refreshToken: string | null;
	isAuthenticated: boolean;
	isLoading: boolean;
	error: string | null;
	requires2FA: boolean;
}

export const initialAuthState: AuthState = {
	user: null,
	accessToken: null,
	refreshToken: null,
	isAuthenticated: false,
	isLoading: false,
	error: null,
	requires2FA: false,
};
